/* The following languages are currently supported for customers */
export enum Language {
  English = 'en',
  Norwegian = 'nb',
  NorwegianNynorsk = 'nn',
  Japanese = 'ja',
  German = 'de',
  Spanish = 'es',
  Catalan = 'ca',
  Icelandic = 'is',
  Swedish = 'sv',
  French = 'fr',
  Portuguese = 'pt',
  Chinese = 'zh',
  Somali = 'so',
  Hmong = 'hmn',
  Vietnamese = 'vi',
  Korean = 'ko',
  Amharic = 'am',
  Hindi = 'hi',
  Armenian = 'hy',
  Russian = 'ru',
}

interface ILanguageToLocale {
  [language: string]: Language
}

export const LanguageLocaleMap: ILanguageToLocale = {
  english: Language.English,
  norwegian: Language.Norwegian,
  'norwegian nynorsk': Language.NorwegianNynorsk,
  japanese: Language.Japanese,
  german: Language.German,
  spanish: Language.Spanish,
  catalan: Language.Catalan,
  icelandic: Language.Icelandic,
  swedish: Language.Swedish,
  french: Language.French,
  portuguese: Language.Portuguese,
  'chinese simplified': Language.Chinese,
  somali: Language.Somali,
  hmong: Language.Hmong,
  vietnamese: Language.Vietnamese,
  korean: Language.Korean,
  amharic: Language.Amharic,
  hindi: Language.Hindi,
  armenian: Language.Armenian,
  russian: Language.Russian,
}

interface ILocaleToLanguage {
  [language: string]: string
}

export const LocaleLanguageMap: ILocaleToLanguage = {
  en: 'English',
  nb: 'Norwegian',
  nn: 'Norwegian Nynorsk',
  ja: 'Japanese',
  de: 'German',
  es: 'Spanish',
  ca: 'Catalan',
  is: 'Icelandic',
  sv: 'Swedish',
  fr: 'French',
  pt: 'Portuguese',
  zh: 'Chinese Simplified',
  so: 'Somali',
  hmong: 'Hmong',
  vietnamese: 'Vietnamese',
  korean: 'Korean',
  amharic: 'Amharic',
  hindi: 'Hindi',
  armenian: 'Armenian',
  russian: 'Russian',
}

/**
 * ITranslationStringsBlueprint
 *
 * Defines the expected structural pattern for a service's collection of
 * base translation strings. Supports recursive Record<string, _>,
 * Record<Enum, _>, and (for lodash.mapKeys) lodash Dictionaries.
 *
 * @see DeriveTranslationKeysFrom
 */
export type ITranslationStringsBlueprint = IPropertyKeyTranslations | IStringKeyTranslations
interface IPropertyKeyTranslations {
  [key: PropertyKey]: IPropertyKeyTranslations | IStringKeyTranslations | string
}
interface IStringKeyTranslations {
  [key: string]: IPropertyKeyTranslations | IStringKeyTranslations | string
}

/**
 * DeriveTranslationKeysFrom<T extends ITranslationStringsBlueprint>
 *
 * Transforms the type of a service's specialized translation strings `T`
 * (including parameters, e.g. `'Hello {{name}}'`) into translation functions
 * (e.g. `({ name: string | number }) => string`), suitable for Translator<T>.
 *
 * @example
 * // const en = {
 * //   validation: {
 * //     minArrayLength: 'At least {{min}} {{label}} must be selected',
 * //   },
 * // } as const satisfies ITranslationStringsBlueprint
 * // type ITranslationStrings = DeriveTranslationKeysFrom<typeof en>
 * // // ITranslationStrings ~= { validation: { greeting: (options: { min: string | number, label: string | number }) => string } }
 * // const translator = new Translator<ITranslationStrings>(en, {}, 'en', () => {})
 * // translator.st.validation.minArrayLength({ min: 123, label: 'foo' })
 */
export type DeriveTranslationKeysFrom<T extends ITranslationStringsBlueprint> = {
  [K in keyof T]: T[K] extends string
    ? TemplateToFunction<T[K]>
    : T[K] extends Record<string, TranslationMapValue>
      ? DeriveTranslationKeysFrom<T[K]>
      : never
}

type TranslationMapValue = string | { [key: string]: TranslationMapValue }

type TemplateToFunction<S extends string> = [ExtractPlaceholders<S>] extends [never]
  ? (options?: { [key: string]: unknown }) => string
  : (options: {
      [K in ExtractPlaceholders<S>]: string | number
    }) => string

type ExtractPlaceholders<S extends string> = S extends `${string}{{${infer Param}}}${string}`
  ? Trim<Param> | ExtractPlaceholders<S extends `${string}{{${string}}}${infer Rest}` ? Rest : never>
  : never

type Trim<S extends string> = S extends ` ${infer R}` ? Trim<R> : S extends `${infer R} ` ? Trim<R> : S
